import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment-timezone';
import { Trans, withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ToastContext, Search, QueryManager, Message as NacreMessage, Loader } from '@weezevent/nacre';

import Zone from '../../models/zone';
import AttendeeModel from '../../models/attendeeModel';

import Loading from '../../components/loading';
import ChartedEvent from '../../components/chartedEvent';
import { Button, Grid, Header, Icon, Message, Segment, Dropdown } from 'semantic-ui-react';
import ActionPicker, { SelectButtons } from './manage/components/attendeesActions';

import ExportButton from '../../components/export/exportButton';
import AttendeesCardManager from './manage/cards';
import AttendeesTable from './manage/table';
import AttendeesMap from './manage/map';

import AttendeesFilters from './manage/components/filters';

import AttendeesActionsManager from './manage/actions/actionManager';
import Sidebar from './manage/sidebar';

import '../../styles/attendees/manage.css';
import RateModel from '../../models/rateModel';
import PartnerModel from '../../models/partner';

const DISPLAY_MODE = {
    cards: AttendeesCardManager,
    table: AttendeesTable,
    map: AttendeesMap
};

class ManageAttendees extends React.Component {
    constructor(...args) {
        super(...args);

        let { display } = this.props.query();
        let displays = Object.keys(DISPLAY_MODE);
        if (!display || displays.indexOf(display) === -1) {
            display = 'cards';
            this.setDisplayModeOnQuery(display);
        }

        this.state = {
            actionError: null,
            attendees: [],
            currentActivePage: 1,
            currentAction: null,
            displayMode: display,
            itemsPerPage: 50,
            onActionDone: null,
            search: null,
            selectedAttendees: [],
            searchValue: '',
            filters: {},
            ordering: [],
            isAttendeesLoading: false,
            noRealInventory: false
        };
        this.scrollListener = null;
        this.loadingNextPage = false;
    }

    /**
     * Get all attendees when mounting
     */
    componentDidMount() {
        // Get all zones in background for actions
        let { organization, event, t } = this.props;

        const organizationId = organization.id;

        if (event.is_seated) {
            Zone.getAll({ organizationId, chartId: event.chart })
                .then(zones => {
                    this.setState({ zones });
                })
                .catch(e => {
                    this.context.error(t('ticket.attendees.manage.zones_retrieval_error'));
                });
        }

        RateModel.getAll({ organizationId, eventId: event.id })
            .then(rates => {
                this.setState({ rates });
            })
            .catch(e => {
                this.context.error(t('ticket.attendees.manage.rates_retrieval_error'));
            });

        PartnerModel.getAll({ organizationId })
            .then(partners => {
                this.setState({ partners });
            })
            .catch(e => {
                this.context.error(t('ticket.attendees.manage.partners_retrieval_error'));
            });

        let query = this.props.query();
        let filters = { ...query };
        delete filters.q;
        delete filters.display;

        let filtersEntries = Object.entries(filters).map(([k, v]) => {
            return [encodeURIComponent(k), encodeURIComponent(v)];
        });

        filters = Object.fromEntries(filtersEntries);

        this.setState(
            {
                searchValue: query.q,
                filters
            },
            () => {
                this.getAllAttendees({ search: query.q, filters });
            }
        );
    }

    /**
     * Preserve same reference for ever
     */
    getScrollListener() {
        if (!this.scrollListener) {
            this.scrollListener = this.handleScroll.bind(this);
        }

        return this.scrollListener;
    }

    /**
     * Add the infinite scroll listener
     */
    addScrollListener() {
        // do not add if already added
        if (this.scrollListener) {
            return;
        }

        window.addEventListener('scroll', this.getScrollListener());
    }

    /**
     * Remove listener to load attendees
     */
    removeScrollListener() {
        window.removeEventListener('scroll', this.getScrollListener());
        this.scrollListener = null;
    }

    /**
     * Deselect all users
     */
    deselect() {
        this.setState({
            currentAction: null,
            selectedAttendees: []
        });
    }

    /**
     * Select all users
     */
    selectAll() {
        this.setState({
            currentAction: null,
            selectedAttendees: this.state.attendees
        });
    }

    /**
     * Sets the display mode
     */
    setDisplayMode(displayMode) {
        if (!(displayMode in DISPLAY_MODE)) {
            throw new Error(`[Attendees Manager] Cannot set mode ${displayMode}`);
        }

        this.setState(
            {
                displayMode,
                onActionDone: null
            },
            () => {
                this.setDisplayModeOnQuery(displayMode);
            }
        );
    }

    /**
     * Sets the display mode on query string URL
     */
    setDisplayModeOnQuery(displayMode) {
        let search = window.location.search;
        let display = '';

        if (search.indexOf('display=') > -1) {
            display = search.replace(/display=[^=]+=?/, `display=${displayMode}`);
        } else {
            let prepend = search ? `${search}&` : '?';
            display = `${prepend}display=${displayMode}`;
        }

        window.history.pushState({}, 'query', display);
    }

    setNoRealInventory(noRealInventory) {
        // method to use in child components
        this.setState({ noRealInventory });
    }

    /**
     * Fetch all attendees
     */
    getAllAttendees({ search, offset = 0, limit = this.state.itemsPerPage, filters, ordering, newPage = false } = {}) {
        search = search || this.state.search;
        filters = filters || this.state.filters;
        ordering = ordering || this.state.ordering;

        let { event, organization, t } = this.props;
        const eventId = event.id;
        const organizationId = organization.id;

        let order = ordering.length ? ordering.join(',') : '-order__sold_date';

        const query = {
            limit,
            offset,
            search,
            order,
            sync: 'False',
            ...filters
        };

        this.setState({
            isAttendeesLoading: true
        });

        AttendeeModel.getAll({ organizationId, eventId }, query)
            .then(attendees => {
                let newAttendees = newPage ? [...this.state.attendees, ...attendees] : attendees;
                this.setState({
                    attendees: newAttendees,
                    filters,
                    ordering,
                    search,
                    isAttendeesLoading: false,
                    attendeesCount: attendees.__meta.headers.get('Total-Count')
                });
            })
            .catch(e => {
                this.setState({
                    isAttendeesLoading: false
                });
                this.context.error(t('ticket.attendees.manage.retrieval_error'));
            })
            .finally(() => {
                this.loadingNextPage = false;
            });
    }

    /**
     * Changes the search value programatically
     */
    changeSearch(searchValue) {
        let search = searchValue instanceof Function ? searchValue(this.state.search) : searchValue;

        this.setState({
            searchValue: search
        });

        return this.handleSearch(search);
    }

    /**
     * Handle searching from search abr
     */
    handleSearch(search = null, newSearchValue = '') {
        this.props.addToQuery('q', search);

        let { searchValue } = this.state;

        if (searchValue && searchValue !== search) {
            this.setState({
                searchValue: ''
            });
        }

        this.setState(
            {
                search
            },
            () =>
                search
                    ? this.getAllAttendees({
                          search: search
                      })
                    : this.getAllAttendees()
        );
    }

    /**
     * Select one attendee
     */
    selectOne(attendee) {
        return this.selectAttendees([attendee]);
    }

    /**
     * Select attendees
     */
    selectAttendees(attendees) {
        let newSelected = this.state.selectedAttendees.slice(0);
        let new_selected = attendees.filter(attendee => {
            if (newSelected.find(selected => selected.id === attendee.id)) {
                return false;
            }

            return true;
        });

        newSelected.push(...new_selected);
        this.setState({
            selectedAttendees: newSelected
        });
    }

    /**
     * DeselectAttendees
     */
    deselectAttendees(attendees) {
        let newSelected = this.state.selectedAttendees.slice(0);
        let deselected = newSelected.filter(attendee => {
            if (attendees.find(att => att.id === attendee.id)) {
                return false;
            }

            return true;
        });

        this.setState({
            selectedAttendees: deselected
        });
    }

    /**
     * Deselect all attendees
     */
    deselectAllAttendees() {
        this.setState({
            selectedAttendees: []
        });
    }

    /**
     * Deselect one attendee
     */
    deselectOne(attendee) {
        let newSelected = this.state.selectedAttendees.slice(0);
        let selectedIndex = this.state.selectedAttendees.findIndex(sattendee => sattendee.id === attendee.id);
        newSelected.splice(selectedIndex, 1);
        this.setState({
            selectedAttendees: newSelected
        });
    }

    /**
     * Handle selecting an attendee
     */
    handleAttendeeSelect({ event, data: { checked } }, attendee, index) {
        event.stopPropagation();

        let selectedIndex = this.state.selectedAttendees.findIndex(sattendee => sattendee.id === attendee.id);
        let selected = selectedIndex > -1;
        if ((selected && checked) || (!selected && !checked)) {
            return;
        }

        let newSelected = this.state.selectedAttendees.slice(0);

        if (checked && !selected) {
            // multiple selection
            // gmail style
            if (event.shiftKey) {
                let [lastSelected] = this.state.selectedAttendees.slice(-1);
                let lastSelectedIndex = this.state.attendees.findIndex(fattendee => fattendee.id === lastSelected.id);
                let selectionIndex = this.state.attendees.findIndex(fattendee => fattendee.id === attendee.id);

                if (selectionIndex < lastSelectedIndex) {
                    // in case they do it backwards... facepalm!
                    [selectionIndex, lastSelectedIndex] = [lastSelectedIndex, selectionIndex];
                }

                newSelected.push(...this.state.attendees.slice(lastSelectedIndex, selectionIndex + 1));
                // make sure we have unique users ;)
                newSelected = Array.from(new Set(newSelected));
            } else {
                return this.selectOne(attendee);
            }
        }

        if (!checked && selected) {
            return this.deselectOne(attendee);
        }

        this.setState({
            selectedAttendees: newSelected
        });
    }

    /**
     * Redirect somewhere
     */
    redirect(somewhere) {
        this.props.history.push(somewhere);
    }

    /**
     * Redirect to attendee page
     */
    goToAttendee(id) {
        const { organization, event } = this.props;
        return this.redirect(`/ticket/O${organization.id}/E${event.id}/seating/attendees/manage/attendee/${id}${window.location.search}`);
    }

    /**
     * Redirect to selling options
     */
    goSell() {
        return this.redirect(`/channels/widget`);
    }

    /**
     * Renders message for no attendees
     */
    renderNoAttendees() {
        return (
            <Message>
                <Trans i18nKey="attendees.noAttendeesPt1">{`Oops, looks like you don't have any attendees yet, `}</Trans>
                <button className="a-button" onClick={() => this.goSell()}>
                    <Trans i18nKey="attendees.noAttendeesPt2">go here</Trans>
                </button>
                <Trans i18nKey="attendees.noAttendeesPt3">{` to sell some tickets!`}</Trans>
            </Message>
        );
    }

    /**
     * Render table for attendees list
     */
    renderAttendeesTable() {
        if (!this.state.attendees) {
            return <Loading />;
        }

        if (!this.state.attendees.length) {
            return this.renderNoAttendees();
        }

        let AttendeesComponent = DISPLAY_MODE[this.state.displayMode];

        return (
            <div className="attendee-manager-window">
                <AttendeesComponent
                    event={this.props.event}
                    attendees={this.state.attendees}
                    addScrollListener={this.addScrollListener.bind(this)}
                    changeSearch={this.changeSearch.bind(this)}
                    deselectOne={this.deselectOne.bind(this)}
                    getAllAttendees={this.getAllAttendees.bind(this)}
                    history={this.props.history}
                    onAttendeeSelected={this.handleAttendeeSelect.bind(this)}
                    onAttendeeClick={this.goToAttendee.bind(this)}
                    onActionDone={callback =>
                        this.setState({
                            onActionDone: callback
                        })
                    }
                    organizationId={this.props.organization.id}
                    pyvarUrl={this.props.apiUrl}
                    removeScrollListener={this.removeScrollListener.bind(this)}
                    selectOne={this.selectOne.bind(this)}
                    selectAttendees={this.selectAttendees.bind(this)}
                    deselectAttendees={this.deselectAttendees.bind(this)}
                    deselectAllAttendees={this.deselectAllAttendees.bind(this)}
                    selectedAttendees={this.state.selectedAttendees}
                    rates={this.state.rates}
                    setNoRealInventory={this.setNoRealInventory.bind(this)}
                />
                {this.state.isAttendeesLoading ? (
                    <Segment basic>
                        <Loader inline="centered" />
                    </Segment>
                ) : null}
            </div>
        );
    }

    /**
     * Render pagination item
     */
    renderDisplayMode() {
        let { displayMode } = this.state;

        return (
            <React.Fragment>
                <Button.Group>
                    {displayMode !== 'map' && (
                        <Button disabled={displayMode === 'table'} icon onClick={() => this.setDisplayMode('table')}>
                            <Icon name="table" />
                        </Button>
                    )}
                    {displayMode !== 'map' && (
                        <Button disabled={displayMode === 'cards'} icon onClick={() => this.setDisplayMode('cards')}>
                            <Icon name="address card outline" />
                        </Button>
                    )}
                    <ChartedEvent event={this.props.event}>
                        <Button disabled={displayMode === 'map'} icon onClick={() => this.setDisplayMode('map')}>
                            <Icon name="map marker" />
                        </Button>
                    </ChartedEvent>
                </Button.Group>
            </React.Fragment>
        );
    }

    /**
     * Go to selected page
     */
    loadNextPage() {
        let { currentActivePage } = this.state;
        if (this.state.attendeesCount <= currentActivePage * this.state.itemsPerPage) {
            return;
        }

        // if we are already loading next page
        if (this.state.isAttendeesLoading || this.state.attendees.length >= this.state.attendeesCount) {
            return;
        }

        let page = ++currentActivePage;
        let offset = (page - 1) * this.state.itemsPerPage;

        this.setState(
            {
                currentActivePage: page
            },
            () =>
                this.getAllAttendees({
                    limit: this.state.itemsPerPage,
                    search: this.state.search,
                    offset,
                    newPage: true
                })
        );
    }

    /**
     * Handle scroll on page
     */
    handleScroll() {
        let scrolledBy = window.scrollY;
        let { scrollHeight } = window.document.body;

        // let's load elements with an offset too soon to be clear and
        // prevent showing the loading spinner ;)
        let offset = 1500;
        if (scrolledBy >= scrollHeight - offset) {
            if (this.loadingNextPage) {
                return;
            }

            this.loadingNextPage = true;
            this.loadNextPage();
        }
    }

    /**
     * Render a useful sidebar for actions & other
     */
    renderSidebar() {
        if (this.state.displayMode === 'table') {
            return null;
        }

        return (
            <Sidebar
                event={this.props.event}
                deselectAll={this.deselect.bind(this)}
                deselectOne={this.deselectOne.bind(this)}
                onActionChange={this.onActionChange.bind(this)}
                selectedAttendees={this.state.selectedAttendees}
                selectAll={this.selectAll.bind(this)}
                goToAttendee={this.goToAttendee.bind(this)}
            />
        );
    }

    /**
     * Triggered when the selected attendees action changes
     */
    onActionChange(action) {
        this.setState({
            currentAction: action
        });
    }

    /**
     * When action is finished
     */
    onActionDone(action, selectedAttendees) {
        this.setState(
            {
                currentAction: null
            },
            () => {
                if (this.state.onActionDone) {
                    this.state.onActionDone(action, selectedAttendees);
                }
            }
        );
    }

    /**
     * If action error
     */
    onActionError(action, selectedAttendees, error) {
        this.setState({
            currentAction: null
        });
    }

    /**
     * Filter attendees from filter component
     */
    filterAttendees(filters) {
        this.getAllAttendees({
            filters
        });
    }

    /**
     * Reset attendees search and fetch all attendees
     */
    resetAttendeesFilters() {
        this.setState(
            {
                filters: {}
            },
            () => {
                this.filterAttendees();
            }
        );
    }

    render() {
        const { t } = this.props;
        let isTable = this.state.displayMode === 'table';
        return (
            <React.Fragment>
                <AttendeesActionsManager
                    action={this.state.currentAction}
                    connectData={this.props.connectData}
                    event={this.props.event}
                    organization={this.props.organization}
                    onActionDone={this.onActionDone.bind(this)}
                    onActionError={this.onActionError.bind(this)}
                    pyvarUrl={this.props.apiUrl}
                    selectedAttendees={this.state.selectedAttendees}
                    rates={this.state.rates}
                    zones={this.state.zones}
                />

                <div>
                    <Header as="h1" className={'header-manage-attendees'}>
                        <Trans i18nKey="attendees.manage">Manage attendees</Trans>
                        {this.state.attendeesCount > 0 ? <span className="attendees-count">{` ${this.state.attendeesCount} total`}</span> : ''}
                        <Dropdown text={t('ticket.export.download_export')} floated="right" disabled={this.state.loading} loading={this.state.loading} className="dropdownExport">
                            <Dropdown.Menu className="dropItem">
                                <ExportButton
                                    organization={this.props.organization}
                                    choiceProcess={'attendees_excel'}
                                    withModal={true}
                                    showButton={false}
                                    onDownloadPreparation={() => this.setState({ loading: true })}
                                    onDownloadLaunch={() => this.setState({ loading: false })}
                                    onError={() => this.context.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.export.download_export_attendees')}
                                    modalBodyTrans={t('ticket.export.export_description_attendee')}
                                    modalTitleTrans={t('ticket.export.export_title_attendee')}
                                    datesFormat={{
                                        sold_start_datetime: date => moment.utc(date),
                                        sold_end_datetime: date => moment.utc(date)
                                    }}
                                />
                                <ExportButton
                                    organization={this.props.organization}
                                    choiceProcess={'attendees_pdf'}
                                    withModal={true}
                                    showButton={false}
                                    onDownloadPreparation={() => this.setState({ loading: true })}
                                    onDownloadLaunch={() => this.setState({ loading: false })}
                                    onError={() => this.context.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.export.download_export_attendees_pdf')}
                                    modalBodyTrans={t('ticket.export.export_description_attendee_pdf')}
                                    modalTitleTrans={t('ticket.export.export_title_attendee_pdf')}
                                    eventIds={[this.props.event.id]}
                                />
                            </Dropdown.Menu>
                        </Dropdown>
                    </Header>

                    <NacreMessage title={t('attendees.messageBackNewGdpTitle')} type="default">
                        <span>
                            <Link className={'link-back-new-gdp'} to={`/ticket/O${this.props.organization.id}/E${this.props.event.id}/manager/attendees`}>
                                {t('ticket.common.clickHere')}
                            </Link>
                            {` ${t('attendees.messageBackNewGdpContent')}`}
                        </span>
                    </NacreMessage>
                    {this.state.noRealInventory && (
                        <NacreMessage title={t('attendees.messageNoInventoryTitle')} type="warning">
                            <span>{` ${t('attendees.messageNoInventoryContent')}`}</span>
                        </NacreMessage>
                    )}

                    <Segment basic className="attendees-filtering-options">
                        <Search
                            className="attendees-search-bar"
                            onChange={(event, value) => this.handleSearch(value)}
                            displayResetIcon={true}
                            handleReset={() => this.handleSearch()}
                            isLoading={this.state.isAttendeesLoading}
                            placeholder={t('attendee.search')}
                            value={this.state.searchValue}
                        />
                        {this.renderDisplayMode()}
                    </Segment>

                    <Segment basic className={`attendees-responsive-select-buttons ${isTable ? 'force-open' : ''}`}>
                        <SelectButtons selectAll={this.selectAll.bind(this)} deselectAll={this.deselect.bind(this)} selectedAttendees={this.state.selectedAttendees} />
                        <ActionPicker event={this.props.event} selectedAttendees={this.state.selectedAttendees} onChange={this.onActionChange.bind(this)} />
                    </Segment>

                    <QueryManager>
                        <AttendeesFilters
                            filters={this.state.filters}
                            event={this.props.event}
                            rates={this.state.rates}
                            zones={this.state.zones}
                            partners={this.state.partners}
                            onChange={filters => this.filterAttendees(filters)}
                            onClear={() => this.resetAttendeesFilters()}
                        />
                    </QueryManager>

                    <Grid columns={16}>
                        <Grid.Column width={isTable ? 16 : 12} className="attendees-list-column">
                            {this.renderAttendeesTable()}
                        </Grid.Column>
                        <Grid.Column width={4} className="attendees-list-sidebar">
                            {this.renderSidebar()}
                        </Grid.Column>
                    </Grid>
                </div>
            </React.Fragment>
        );
    }
}

ManageAttendees.contextType = ToastContext;

ManageAttendees.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    contingents: PropTypes.object,
    history: PropTypes.object.isRequired,
    inventories: PropTypes.array,
    query: PropTypes.func.isRequired,
    addToQuery: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    apiUrl: PropTypes.string.isRequired,
    connectData: PropTypes.object.isRequired
};

export default withTranslation()(ManageAttendees);
